import firebase from 'firebase/app';
import 'firebase/firestore';
import { uniqBy } from 'lodash';
import { uid } from 'uid';
import axios from 'axios';

const state = {
  truckerOnboardingDocs: [],
  onboardingDetails: [],
};
const mutations = {
  set_onboarding_details: (state, payload) => {
    state.onboardingDetails = payload;
  },
};
const actions = {
  async storeOnboardingDocuments({ commit, state, rootGetters }, payload) {
    const {
      files,
      truckerOrgId,
      exporterOrgId,
      scac,
      email,
      truckerCompany,
      exporterName,
      exporterUser,
    } = payload;
    const id = `${truckerOrgId}_${exporterOrgId}`;

    const documents = [];
    const attachments = [];

    const docRef = firebase
      .firestore()
      .collection('onboarding_documents')
      .doc(id);

    const doc = await docRef.get();
    let selectedItem = null;
    if (!doc.exists) {
      selectedItem = {
        truckerOrgId: truckerOrgId,
        exporterOrgId: exporterOrgId,
        truckerScac: scac,
        truckerEmail: email,
        truckerCompany,
        id,
      };
      await docRef.set(selectedItem, { merge: true });
    }

    try {
      await Promise.all(
        await files.map(async (file) => {
          const storageRef = firebase.storage().ref();

          const fileId = file.name;

          const fileRef = await storageRef.child(
            `uploads/trucker/${truckerOrgId}/${fileId}`
          );
          const snapshot = await fileRef.put(file);

          const url = await snapshot.ref.getDownloadURL();

          const id = uid();
          const payload = {
            filename: file.name,
            path: url,
            type: file.type,
            status: 'pending',
            id: id,
          };
          documents.push(payload);
          await docRef.collection('documents').doc(id).set(payload);

          attachments.push({ filename: file.name, path: url });
        })
      );

      // sending in-app and email notifications
      await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/onboardingDocuments/docCreated/sendNotification`,
        {
          exporterOrgId,
          truckerCompany,
          attachments,
          exporterName,
          exporterUser,
        }
      );
      const committedExporterOrgs = state.onboardingDetails;
      committedExporterOrgs.forEach((obj) => {
        if (obj.orgId === exporterOrgId && obj.truckerOrgId === truckerOrgId) {
          obj.truckerOnboardingDocuments.push(...documents);
        }
      });
      await commit('set_onboarding_details', committedExporterOrgs);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  },
  async deleteOnboardingDocument({ commit, rootGetters }, payload) {
    const { selectedItem, document } = payload;
    const onBoardingDocumentId = `${selectedItem.truckerOrgId}_${selectedItem.orgId}`;
    const storageRef = firebase.storage().ref();
    const fileRef = await storageRef.child(
      `uploads/trucker/${selectedItem.truckerOrgId}/${document.filename}`
    );
    try {
      await fileRef.delete();
    } catch (error) {
      console.error('Error deleting file: ', error);
    }
    const docRef = firebase
      .firestore()
      .collection('onboarding_documents')
      .doc(onBoardingDocumentId)
      .collection('documents')
      .doc(document.id);
    try {
      await docRef.delete();
      const emailData = {
        status: 'removed',
        attachments: [document],
        exporterOrgId: selectedItem.orgId,
        truckerOrgId: selectedItem.truckerOrgId,
        exporterName: selectedItem.name,
        exporterUser: selectedItem.owner,
      };

      await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/onboardingDocuments/docUpdated/sendNotification`,
        emailData
      );
    } catch (error) {
      console.error('Error deleting document: ', error);
    }
  },
  async getCommittedExporterOrgs({ commit, rootGetters, dispatch }, payload) {
    const truckerCommitments = await rootGetters[
      'bookings/get_trucker_commitments'
    ];
    const orgdata = await rootGetters['auth/get_org'];
    const approvedCommitments = truckerCommitments.filter((val) =>
      val.timeLine?.some(({ status }) => status === 'approved')
    );

    const committedExporters = uniqBy(approvedCommitments, 'orgId');

    await Promise.all(
      committedExporters.map(async (val) => {
        const orgId = val.orgId;
        let isScacPresent = false;
        let onBoardingDocs = [];
        const id = `${val.truckerOrgId}_${orgId}`;
        const { preferredTruckerScacs, company } = await dispatch(
          'getPreferredTruckerScacs',
          orgId
        );

        if (preferredTruckerScacs.length) {
          isScacPresent = preferredTruckerScacs?.includes(orgdata.scac);
        }

        if (!isScacPresent) {
          const { onBoardingDocuments } =
            (
              await firebase
                .firestore()
                .collection('trucker_requirements')
                .doc(orgId)
                .get()
            ).data() || [];
          onBoardingDocs = onBoardingDocuments || [];
        }

        const documents = await firebase
          .firestore()
          .collection('onboarding_documents')
          .doc(id)
          .collection('documents')
          .get();

        val.truckerOnboardingDocuments =
          documents.docs?.map((doc) => doc.data()) || [];

        val.ExporterOnboardingDocuments = onBoardingDocs;
        val.company = company;
      })
    );

    // firebase
    //   .firestore()
    //   .collection('onboarding_requirments')
    //   .where('truckerOrgId', '==', rootGetters['auth/get_user'].orgId)
    //   .get();

    await commit('set_onboarding_details', committedExporters);
    return committedExporters;
  },
  async getUploadedDocs({ commit, rootGetters }, payload) {
    const id = `${payload.truckerOrgId}_${payload.orgId}`;
    const { documents } = (await (
      await firebase
        .firestore()
        .collection('onboarding_documents')
        .doc(id)
        .get()
    ).data()) || { documents: [] };
    return documents;
  },

  // Getting preffered trucker scacs based on exporter orgId
  async getPreferredTruckerScacs({ commit, rootGetters }, orgId) {
    try {
      const orgDataRef = await firebase
        .firestore()
        .collection('organizations')
        .doc(orgId)
        .get();

      const orgData = orgDataRef.data() || {};
      const preferredTruckers = orgData.preferredTruckers || [];

      const preferredTruckerScacs = preferredTruckers.map((obj) => obj.scac);

      return {
        preferredTruckerScacs,
        company: orgData.company || '',
      };
    } catch (error) {
      return {
        preferredTruckerScacs: [],
        company: '',
      };
    }
  },
};
const getters = {
  get_onboarding_details: (state) => {
    return state.onboardingDetails;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
